import { createAsyncThunk } from '@reduxjs/toolkit';
import Relay from 'v2/services/relay';
import { patchData, postData } from 'services/clinkHelpers';
import { httpHelperV2 } from 'v2/services/httpHelper';
import flag from 'v2/helpers/flags';

const relay = new Relay('relay', '', '');
const fetchOrders = createAsyncThunk('order/fetchOrders', async (pid) =>
  relay
    .get('', { action: 'order', method: 'fetchAll', pid })
    .then((result) => (result.status === 200 ? result.json() : result.status))
    .catch((error) => error.status),
);

const fetchLoiLogs = createAsyncThunk(
  'order/fetchLoiLogs',
  async ({ pid, loiId }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${loiId}/logs`,
      method: 'GET',
    }),
);

const deleteOrder = createAsyncThunk(
  'order/deleteOrder',
  async ({ qid, tid, pid, did }) =>
    relay
      .deleter('', {
        action: 'order',
        method: 'remove',
        qid,
        tid,
        pid,
        ...((flag('DELETE_DRAFT_ORDER') && { did }) || {}),
      })
      .then((result) => (result.status === 200 ? result.json() : result.status))
      .catch((error) => error.status),
);

const withdrawSentOrder = createAsyncThunk(
  'order/withdrawSentOrder',
  async ({ pid, id, tid, did }) =>
    patchData(
      'transaction',
      'withdrawOrder',
      {},
      {
        pid,
        id,
        tid,
        ...((flag('DELETE_DRAFT_ORDER') && { did }) || {}),
      },
    )
      .then((result) => (result.status === 200 ? result.json() : result.status))
      .catch((error) => error.status),
);

const markAsSignOrder = createAsyncThunk(
  'order/markAsSignOrder',
  async ({ pid, id, tid }) =>
    patchData(
      'transaction',
      'markAsSigned',
      {},
      {
        pid,
        id,
        tid,
      },
    )
      .then((result) => (result.status === 200 ? result.json() : result.status))
      .catch((error) => error.status),
);

const sendApprovalReminder = createAsyncThunk(
  'order/sendApprovalReminder',
  async ({ approver_id, did }) =>
    postData(
      'order',
      'approvalReminder',
      { data: { approver_id: parseInt(approver_id, 10) } },
      {
        did,
      },
    )
      .then((result) => result.json())
      .catch((error) => error.status),
);

const assignOrderApprovers = createAsyncThunk(
  'order/assignOrderApprovers',
  async ({ did, data }) => {
    return httpHelperV2({
      url: `document/${did}/order/approvers`,
      method: 'POST',
      body: data,
    });
  },
);

const rejectionAcknowledge = createAsyncThunk(
  'order/rejectionAcknowledge',
  async ({ did, data }) => {
    return httpHelperV2({
      url: `document/${did}/order/rejection-acknowledge`,
      method: 'POST',
      body: data,
    });
  },
);

const withdrawOrderApproval = createAsyncThunk(
  'order/withdrawOrderApproval',
  async ({ did }) => {
    return httpHelperV2({
      url: `document/${did}/order/approval/withdraw`,
      method: 'POST',
    });
  },
);
const assignedOrderApprovers = createAsyncThunk(
  'order/assignedOrderApprovers',
  async (pid) => {
    return httpHelperV2({
      url: `project/${pid}/order/assigned-approvers`,
      method: 'GET',
    });
  },
);
const approveOrRejectOrder = createAsyncThunk(
  'order/approveOrRejectOrder',
  async ({ did, approver_id, status, comment = '', signature, meta }) => {
    const body = {
      approver_id,
      status,
      comment,
      ...(meta?.signatory && { signing_mechanism: signature }),
    };

    return httpHelperV2({
      url: `document/${did}/order/approval`,
      method: 'POST',
      body,
    });
  },
);

const approveOrRejectLoi = createAsyncThunk(
  'order/approveOrRejectLoi',
  async ({ pid, did, approver_id, status, comment = '' }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/approval/${approver_id}`,
      method: 'PUT',
      body: {
        status,
        ...(status === 'Rejected' && { comment }),
      },
    }),
);

const assignedApproversforDocument = createAsyncThunk(
  'order/assignedApproversforDocument',
  async ({ pid, did, entity = 'order' }) =>
    httpHelperV2({
      url: `project/${pid}/${entity}/assigned-approvers?did=${did}`,
      method: 'GET',
    }),
);

const assignedLoiApprovers = createAsyncThunk(
  'order/assignedLoiApprovers',
  async ({ pid, did }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/assigned-approvers`,
      method: 'GET',
    }),
);

const fetchOrderLogs = createAsyncThunk(
  'order/fetchOrderLogs',
  async (document_id) =>
    httpHelperV2({
      url: `document/${document_id}/order/logs`,
      method: 'GET',
    }),
);

const fetchProjectLois = createAsyncThunk(
  'order/fetchProjectLois',
  async (pid) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent`,
      method: 'GET',
    }),
);

/**
 * Assign approvers to an LOI, moving it from Draft/Rejected to Pending.
 *
 * Requested from the document creator, which owns the approver-selection UI.
 * The body is a bare array of approver rows; the path parameter is the LOI
 * *document* id, as with every LOI sub-route.
 */
const assignLoiApprovers = createAsyncThunk(
  'order/assignLoiApprovers',
  async ({ pid, did, data }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/approvals`,
      method: 'POST',
      body: data,
    }),
);

const withdrawLoiApproval = createAsyncThunk(
  'order/withdrawLoiApproval',
  async ({ pid, did }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/approvals/withdraw`,
      method: 'DELETE',
    }),
);

const sendLoiApprovalReminder = createAsyncThunk(
  'order/sendLoiApprovalReminder',
  async ({ pid, did, approver_id }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/approvals/${approver_id}/reminder`,
      method: 'POST',
    }),
);

const rejectionAcknowledgeLoi = createAsyncThunk(
  'order/rejectionAcknowledgeLoi',
  async ({ pid, did }) =>
    httpHelperV2({
      url: `project/${pid}/letter_of_intent/${did}/rejection-acknowledge`,
      method: 'POST',
    }),
);

export {
  fetchOrders,
  fetchLoiLogs,
  deleteOrder,
  withdrawSentOrder,
  markAsSignOrder,
  sendApprovalReminder,
  assignOrderApprovers,
  withdrawOrderApproval,
  assignedOrderApprovers,
  assignedApproversforDocument,
  assignedLoiApprovers,
  rejectionAcknowledge,
  approveOrRejectOrder,
  approveOrRejectLoi,
  fetchOrderLogs,
  fetchProjectLois,
  assignLoiApprovers,
  withdrawLoiApproval,
  rejectionAcknowledgeLoi,
  sendLoiApprovalReminder,
};
